import { useCallback, useSyncExternalStore } from 'react';

import { useBranding } from './branding';

/**
 * R1 — THEME IS PALETTE x MODE.
 *
 * `data-palette` on <html> (absent = Default, "sage" = Sage) and the `.dark` class, exactly
 * as the token file's specificity map expects. Four combinations, all of them real.
 *
 * The DEFAULTS come from `GET /api/branding` (`defaultPalette` / `defaultMode`), so an
 * operator's choice applies to everyone including signed-out visitors. A person's own
 * choice overrides that and persists client-side only — it is a rendering preference, not a
 * fact about the account, so it never goes near the server.
 */

export type Palette = 'default' | 'sage';
export type Mode = 'light' | 'dark';

interface Override {
  readonly palette: Palette | null;
  readonly mode: Mode | null;
}

const PALETTE_KEY = 'accounts.palette';
const MODE_KEY = 'accounts.mode';

function readStored(): Override {
  try {
    const palette = window.localStorage.getItem(PALETTE_KEY);
    const mode = window.localStorage.getItem(MODE_KEY);
    return {
      palette: palette === 'default' || palette === 'sage' ? palette : null,
      mode: mode === 'light' || mode === 'dark' ? mode : null,
    };
  } catch {
    // Storage refused (private mode, blocked cookies): fall back to the branding defaults.
    return { palette: null, mode: null };
  }
}

let snapshot: Override = readStored();
const listeners = new Set<() => void>();

function emit(next: Override): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Override {
  return snapshot;
}

function persist(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A preference that cannot be stored still applies for this page's lifetime.
  }
}

/** Apply the resolved theme to <html>. The only place either handle is written. */
export function applyTheme(palette: Palette, mode: Mode): void {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  if (palette === 'sage') root.setAttribute('data-palette', 'sage');
  else root.removeAttribute('data-palette');
}

export interface Appearance {
  readonly palette: Palette;
  readonly mode: Mode;
  readonly setPalette: (palette: Palette) => void;
  readonly setMode: (mode: Mode) => void;
}

/** Resolved appearance: the person's stored choice, otherwise the configured default. */
export function useAppearance(): Appearance {
  const override = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const branding = useBranding();

  const palette: Palette = override.palette ?? branding.data?.defaultPalette ?? 'default';
  const mode: Mode = override.mode ?? branding.data?.defaultMode ?? 'light';

  const setPalette = useCallback((next: Palette) => {
    persist(PALETTE_KEY, next);
    emit({ ...snapshot, palette: next });
  }, []);

  const setMode = useCallback((next: Mode) => {
    persist(MODE_KEY, next);
    emit({ ...snapshot, mode: next });
  }, []);

  return { palette, mode, setPalette, setMode };
}
